export type Stack = "html" | "react-vite" | "astro";
export type Coupling = "tight" | "middle" | "loose";

export const STACK_LABELS: Record<Stack, string> = {
  html: "HTML / CSS / JS",
  "react-vite": "React + Vite",
  astro: "Astro",
};

export const COUPLING_LABELS: Record<Coupling, string> = {
  tight: "Tight",
  middle: "Middle",
  loose: "Loose",
};

export interface ShellCombination {
  integrationCode: string;
  lang: string;
  claudePrompt: string;
}

const SUFFIX =
  "After completing, add the dg-connectors.json entry shown in the brief. Confirm each step is complete before moving to the next.";

const PLATFORM_URL = "https://apps.jensen-dowell.me";

const HTML_MIDDLE_CODE = `Download shell files from the platform:
${PLATFORM_URL}/api/v1/shell/v1/script.js → public/shell-v1.js
${PLATFORM_URL}/api/v1/shell/v1/styles.css → public/shell-v1.css

<!-- in <head> -->
<meta name="dg-app-name" content="__APP_NAME__">
<link rel="stylesheet" href="/shell-v1.css">

<!-- before </body> -->
<script defer src="/shell-v1.js"></script>`;

const ASTRO_MIDDLE_CODE = `Download shell files from the platform:
${PLATFORM_URL}/api/v1/shell/v1/script.js → public/shell-v1.js
${PLATFORM_URL}/api/v1/shell/v1/styles.css → public/shell-v1.css

<!-- src/layouts/Layout.astro — in <head>: -->
<meta name="dg-app-name" content="__APP_NAME__">
<link rel="stylesheet" href="/shell-v1.css">

<!-- src/layouts/Layout.astro — before </body>: -->
<script src="/shell-v1.js"></script>`;

export const PLATFORM_SHELL: Record<
  Stack,
  Record<Coupling, ShellCombination>
> = {
  html: {
    tight: {
      lang: "html",
      integrationCode: `<!-- in <head> -->
<meta name="dg-app-name" content="__APP_NAME__">
<link rel="stylesheet" href="${PLATFORM_URL}/api/v1/shell/v1/styles.css">

<!-- before </body> -->
<script defer src="${PLATFORM_URL}/api/v1/shell/v1/script.js"></script>`,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this HTML/CSS/JS app using the Tight coupling method. The platform is at ${PLATFORM_URL}. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    middle: {
      lang: "html",
      integrationCode: HTML_MIDDLE_CODE,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this HTML/CSS/JS app using the Middle coupling method. Download the shell files, place them in /public/, and reference them locally. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    loose: {
      lang: "html",
      integrationCode: HTML_MIDDLE_CODE,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this HTML/CSS/JS app using the Loose coupling method. Download the shell files, place them in /public/, and reference them locally. No future updates are expected. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
  },
  "react-vite": {
    tight: {
      lang: "tsx",
      integrationCode: `// package.json dependency:
"@dg/shell": "github:__OWNER__/__REPO__#main"

// App.tsx:
import { PlatformShell } from '@dg/shell/react'
export default function App() {
  return <PlatformShell appName="__APP_NAME__">{/* content */}</PlatformShell>
}`,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this React + Vite app using the Tight coupling method. Install the @dg/shell package from the GitHub main branch. Wrap the app in <PlatformShell>. Replace __APP_NAME__ and __OWNER__/__REPO__ with actual values. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    middle: {
      lang: "tsx",
      integrationCode: `// package.json dependency:
"@dg/shell": "github:__OWNER__/__REPO__#shell-v1.0.0"

// App.tsx:
import { PlatformShell } from '@dg/shell/react'
export default function App() {
  return <PlatformShell appName="__APP_NAME__">{/* content */}</PlatformShell>
}`,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this React + Vite app using the Middle coupling method. Install the @dg/shell package pinned to the shell-v1.0.0 tag. Wrap the app in <PlatformShell>. Replace __APP_NAME__ and __OWNER__/__REPO__ with actual values. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    loose: {
      lang: "text",
      integrationCode: `Copy packages/shell/src/react/ into your project as src/shell/
Import from './shell/react' instead of '@dg/shell/react'

// App.tsx:
import { PlatformShell } from './shell/react'
export default function App() {
  return <PlatformShell appName="__APP_NAME__">{/* content */}</PlatformShell>
}`,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this React + Vite app using the Loose coupling method. Copy the shell source into the project rather than installing the package. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
  },
  astro: {
    tight: {
      lang: "html",
      integrationCode: `<!-- src/layouts/Layout.astro — in <head>: -->
<meta name="dg-app-name" content="__APP_NAME__">
<link rel="stylesheet" href="${PLATFORM_URL}/api/v1/shell/v1/styles.css">

<!-- src/layouts/Layout.astro — before </body>: -->
<script src="${PLATFORM_URL}/api/v1/shell/v1/script.js"></script>`,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this Astro app using the Tight coupling method. The platform is at ${PLATFORM_URL}. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    middle: {
      lang: "html",
      integrationCode: ASTRO_MIDDLE_CODE,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this Astro app using the Middle coupling method. Download the shell files, place them in /public/, and reference them locally. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
    loose: {
      lang: "html",
      integrationCode: ASTRO_MIDDLE_CODE,
      claudePrompt: `Read the attached brief in full. Integrate the Platform Shell connector into this Astro app using the Loose coupling method. Download the shell files, place them in /public/, and reference them locally. No future updates are expected. Replace __APP_NAME__ with the actual app name. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
    },
  },
};
